import { useEffect, useState } from 'react';
import { useBrandOptions } from '@/hooks/useBrandOptions';
import { api } from '@/services/api';
import type { Audience, Category, GarmentType } from '@/types/domain';

/**
 * Single source of truth for the shared portion of the comparison form —
 * audience, garment group/category, garment type, known source brand/size —
 * used by both EquivalentSizesPage and BrandVsBrandPage. Previously this
 * state (and its reset-on-change effects) was duplicated independently in
 * each page; this hook is the fix, so garmentType doesn't become a third
 * copy of logic that has to be kept in sync by hand.
 *
 * Each page still owns its own downstream state (results, selected match,
 * target brand) since that genuinely differs page to page.
 */
export function useComparisonForm() {
  const [audience, setAudience] = useState<Audience>('women');
  const [category, setCategory] = useState<Category>('tops');
  const [garmentType, setGarmentType] = useState<GarmentType | null>(null);
  const [sourceBrandSlug, setSourceBrandSlug] = useState('');
  const [sourceSize, setSourceSize] = useState('');
  const [sourceSizes, setSourceSizes] = useState<string[]>([]);

  const { brands, loading: brandsLoading } = useBrandOptions(audience, category);

  // Reset downstream selections when audience/category changes — a brand or
  // size valid for one combination usually isn't valid for another, and a
  // garment type only makes sense within the group it belongs to.
  useEffect(() => {
    setSourceBrandSlug('');
    setSourceSize('');
    setSourceSizes([]);
    setGarmentType(null);
  }, [audience, category]);

  // Load available sizes for the chosen source brand.
  useEffect(() => {
    if (!sourceBrandSlug) {
      setSourceSizes([]);
      return;
    }
    let cancelled = false;
    api.getChart(sourceBrandSlug, audience, category).then((res) => {
      if (cancelled) return;
      if (res.success) {
        const data = res.data as { rows: { label: string }[] };
        setSourceSizes(data.rows.map((r) => r.label));
      } else {
        setSourceSizes([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sourceBrandSlug, audience, category]);

  return {
    audience,
    setAudience,
    category,
    setCategory,
    garmentType,
    setGarmentType,
    sourceBrandSlug,
    setSourceBrandSlug,
    sourceSize,
    setSourceSize,
    sourceSizes,
    brands,
    brandsLoading,
  };
}
